import * as React from 'react'
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { whatsNewUnreadQuery } from '@/api/help'
import {
  Bell, Building2, Check, ChevronDown, ChevronsUpDown, Code2, Globe, Layers, LifeBuoy, LogOut, Menu as MenuIcon, Monitor, Moon, Plug,
  Search, Settings, Shield, Sparkles, Sun, UserRound, Users, Waypoints, X, Plus, Brain,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { authClient } from '@/lib/auth-client'
import { api, unwrap, errorMessage } from '@/lib/api'
import { getTheme, setTheme, type ThemeChoice } from '@/lib/theme'
import { toast } from 'sonner'
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger, Tooltip, Kbd } from '@/components/ui/controls'
import { Button } from '@/components/ui/button'
import { GROUPS, GROUP_LABEL, useAllScreens, useCurrentScreen, type Group, type NavScreen } from './screens'
import { rememberWorkspace, useSession, useWorkspaceId, type Session } from './session'
import { CommandPalette } from './CommandPalette'
import { PlanHub } from '@/screens/billing/plan-hub'
import { NotificationsBell } from '@/screens/me/notifications-bell'

const GROUP_ICON: Record<Group, React.ComponentType<{ className?: string }>> = {
  Me: UserRound,
  Workspace: Layers,
  Investigate: Waypoints,
  Improve: Sparkles,
  Connect: Plug,
  Organisation: Users,
  Govern: Shield,
  Develop: Code2,
  Help: LifeBuoy,
  Platform: Globe,
}

function initials(name: string, email: string): string {
  const src = name.trim() || email
  const parts = src.split(/[\s@._-]+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?'
}

export function Avatar({ name, email, image, size = 30, className }: { name: string; email: string; image?: string | null; size?: number; className?: string }) {
  return image ? (
    <img src={image} alt="" width={size} height={size} className={cn('shrink-0 rounded-full object-cover', className)} style={{ width: size, height: size }} />
  ) : (
    <span className={cn('grid shrink-0 place-items-center rounded-full bg-accent text-[11px] font-bold text-accent-ink', className)} style={{ width: size, height: size }} aria-hidden>
      {initials(name, email)}
    </span>
  )
}

/** Visible screens for this person, by group. */
function useNav(session: Session) {
  const all = useAllScreens()
  const perms = new Set(session.permissions as string[])
  const can = (s: NavScreen) => {
    if (s.platform) return session.is_super_admin
    if (!s.perm) return true
    return (Array.isArray(s.perm) ? s.perm : [s.perm]).some(p => perms.has(p))
  }
  const byGroup = new Map<Group, NavScreen[]>()
  for (const g of GROUPS) byGroup.set(g, [])
  for (const s of all) if (!s.hidden && can(s)) byGroup.get(s.group)?.push(s)
  return byGroup
}

/** A screen's link: workspace-scoped paths take the current workspace. */
export function screenHref(s: { fullPath: string }, ws: string | null): string {
  return s.fullPath.replace('$ws', ws ?? '_')
}

export function AppShell() {
  const session = useSession()
  const nav = useNav(session)
  const current = useCurrentScreen()
  const ws = useWorkspaceId()
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [paletteOpen, setPaletteOpen] = React.useState(false)
  const pathname = useRouterState({ select: s => s.location.pathname })

  React.useEffect(() => setMobileOpen(false), [pathname])
  React.useEffect(() => {
    if (ws) rememberWorkspace(ws)
  }, [ws])
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(o => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const groups = GROUPS.filter(g => (nav.get(g)?.length ?? 0) > 0)
  const activeGroup: Group = current?.group && groups.includes(current.group) ? current.group : 'Workspace'

  return (
    <div className="flex h-dvh overflow-hidden bg-bg">
      {/* Rail */}
      <aside className="hidden w-[64px] shrink-0 flex-col items-center gap-1 bg-rail py-3 md:flex" aria-label="Areas">
        <Link to="/" className="mb-3 grid size-9 place-items-center rounded-xl bg-accent text-[15px] font-black text-accent-ink" aria-label="ESM home">
          <Brain className="size-5" />
        </Link>
        {groups.map(g => {
          const Icon = GROUP_ICON[g]
          const first = nav.get(g)![0]!
          const active = current?.group === g
          return (
            <Tooltip key={g} content={GROUP_LABEL[g] === 'Org' ? 'Organisation' : GROUP_LABEL[g]} side="right">
              <Link
                to={screenHref(first, ws)}
                className={cn('flex w-[52px] flex-col items-center gap-0.5 rounded-xl py-1.5 text-[9.5px] font-semibold transition-colors', active ? 'bg-white/12 text-white' : 'text-rail-ink/80 hover:bg-white/8 hover:text-white')}
                aria-current={active ? 'page' : undefined}
              >
                <Icon className="size-[18px]" />
                <span>{GROUP_LABEL[g]}</span>
              </Link>
            </Tooltip>
          )
        })}
        <div className="flex-1" />
        <ThemeButton />
        <UserMenu session={session} />
      </aside>

      {/* Sidebar */}
      <div className={cn('fixed inset-0 z-40 bg-[#0b1425]/40 md:hidden', mobileOpen ? 'block' : 'hidden')} onClick={() => setMobileOpen(false)} aria-hidden />
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-[264px] shrink-0 flex-col border-r border-line bg-surface transition-transform md:static md:z-auto md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Screens"
      >
        <div className="flex items-center justify-between px-4 pb-2 pt-4">
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-extrabold tracking-tight text-ink">ESM</span>
            <span className="rounded bg-accent-soft px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-accent">ADYA</span>
          </div>
          <Button variant="ghost" size="icon-sm" className="md:hidden" aria-label="Close navigation" onClick={() => setMobileOpen(false)}>
            <X />
          </Button>
        </div>
        <WorkspaceSwitcher session={session} />
        <nav className="flex-1 overflow-y-auto px-2 pb-4 scrollbar-thin">
          {groups.map(g => (
            // Desktop: only the area chosen in the rail. Phone (no rail): every area.
            <div key={g} className={cn('mt-3', g !== activeGroup && 'md:hidden')}>
              <div className="px-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted">{g === 'Investigate' ? 'Trace' : g}</div>
              {nav.get(g)!.map(s => {
                const href = screenHref(s, ws)
                const active = current?.routeId === s.routeId
                const Icon = s.icon
                return (
                  <Link
                    key={s.routeId}
                    to={href}
                    className={cn('flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-[12.5px] transition-colors', active ? 'bg-accent-soft font-semibold text-accent' : 'text-ink hover:bg-soft')}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon className={cn('size-[15px] shrink-0', active ? 'text-accent' : 'text-muted')} />
                    <span className="truncate">{s.title}</span>
                  </Link>
                )
              })}
            </div>
          ))}
        </nav>
        <div className="border-t border-line p-3 md:hidden">
          <UserMenu session={session} inline />
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-[52px] shrink-0 items-center gap-2 border-b border-line bg-surface px-3 md:px-4">
          <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open navigation" onClick={() => setMobileOpen(true)}>
            <MenuIcon />
          </Button>
          <PlanHub />
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="ml-auto flex h-8 w-full max-w-[300px] items-center gap-2 rounded-lg border border-line bg-bg px-2.5 text-[12px] text-muted hover:border-accent md:ml-4"
          >
            <Search className="size-3.5" />
            <span className="flex-1 text-left">Search ESM</span>
            {/* A keyboard shortcut means nothing on a phone or tablet. */}
            <span className="max-sm:hidden pointer-coarse:hidden">
              <Kbd>Ctrl K</Kbd>
            </span>
          </button>
          <span className="hidden items-center gap-1.5 rounded-lg bg-teal/10 px-2 py-1 text-[11px] font-semibold text-teal lg:flex" title="Your data stays in this country">
            <Globe className="size-3.5" />
            {session.data_plane.region} · {session.data_plane.name}
          </span>
          <NotificationsBell />
          <HelpButton screenId={current?.id} />
        </header>
        {/* `relative` makes main the containing block for absolutely positioned
            hidden inputs and sr-only text, so they scroll with the page instead
            of stretching the document past the window. */}
        <main className="relative min-h-0 flex-1 overflow-y-auto" id="main">
          <Outlet />
        </main>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  )
}

/** Help for the current screen; a dot when What’s new has unseen entries. */
function HelpButton({ screenId }: { screenId?: string }) {
  const unread = useQuery({ ...whatsNewUnreadQuery(), retry: false })
  const count = unread.data?.unread ?? 0
  return (
    <Tooltip content={count ? `Help · ${count} new update${count === 1 ? '' : 's'}` : 'Help for this screen'}>
      <Link to="/help" search={(screenId ? { screen: screenId } : {}) as never} className="relative hidden md:block">
        <Button variant="ghost" size="icon" aria-label={count ? `Help, ${count} new updates` : 'Help'}>
          <LifeBuoy />
        </Button>
        {count > 0 && <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-accent" aria-hidden />}
      </Link>
    </Tooltip>
  )
}

function ThemeButton() {
  const [t, setT] = React.useState<ThemeChoice>(getTheme())
  const next: Record<ThemeChoice, ThemeChoice> = { system: 'light', light: 'dark', dark: 'system' }
  const Icon = t === 'light' ? Sun : t === 'dark' ? Moon : Monitor
  return (
    <Tooltip content={`Theme: ${t}. Click to change.`} side="right">
      <button
        type="button"
        aria-label={`Theme: ${t}. Change theme`}
        className="mb-1 grid size-9 place-items-center rounded-xl text-rail-ink/80 hover:bg-white/8 hover:text-white"
        onClick={() => {
          setTheme(next[t])
          setT(next[t])
        }}
      >
        <Icon className="size-[17px]" />
      </button>
    </Tooltip>
  )
}

function WorkspaceSwitcher({ session }: { session: Session }) {
  const ws = useWorkspaceId()
  const navigate = useNavigate()
  const current = useCurrentScreen()
  const active = session.workspaces.find(w => w.id === ws)
  const routes = useAllScreens()
  const go = (id: string) => {
    rememberWorkspace(id)
    const here = routes.find(r => r.routeId === current?.routeId)
    if (here && here.fullPath.includes('$ws')) void navigate({ to: here.fullPath.replace('$ws', id) })
    else void navigate({ to: `/w/${id}/overview` })
  }
  return (
    <div className="px-3 pb-2">
      <Menu>
        <MenuTrigger asChild>
          <button type="button" className="flex w-full items-center gap-2.5 rounded-xl border border-line bg-bg px-2.5 py-2 text-left hover:border-accent">
            <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-accent-soft text-accent">
              <Building2 className="size-3.5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12px] font-semibold text-ink">{active?.name ?? 'No workspace'}</span>
              <span className="block truncate text-[10.5px] text-muted">
                {session.org?.name} · {active?.visibility === 'shared' ? 'Shared' : 'Private'}
              </span>
            </span>
            <ChevronsUpDown className="size-3.5 shrink-0 text-muted" />
          </button>
        </MenuTrigger>
        <MenuContent align="start" className="w-[240px]">
          <MenuLabel>Workspaces in {session.org?.name}</MenuLabel>
          {session.workspaces.map(w => (
            <MenuItem key={w.id} onSelect={() => go(w.id)}>
              {w.id === ws ? <Check className="!text-accent" /> : <span className="size-3.5" />}
              <span className="truncate">{w.name}</span>
              {!w.member && <span className="ml-auto text-[10px] text-muted">admin view</span>}
            </MenuItem>
          ))}
          <MenuSeparator />
          <MenuItem onSelect={() => void navigate({ to: '/org/workspaces', search: { new: 1 } as never })}>
            <Plus /> New workspace
          </MenuItem>
          <MenuItem onSelect={() => void navigate({ to: '/org/workspaces' })}>
            <Settings /> Manage workspaces
          </MenuItem>
        </MenuContent>
      </Menu>
    </div>
  )
}

function UserMenu({ session, inline }: { session: Session; inline?: boolean }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const switchOrg = async (orgId: string) => {
    try {
      await unwrap(api.POST('/esm/v1/session/active-org', { body: { org_id: orgId } }))
      qc.clear()
      window.location.assign('/')
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }
  const signOut = async () => {
    await authClient.signOut().catch(() => undefined)
    qc.clear()
    window.location.assign('/login?signed_out=1')
  }
  return (
    <Menu>
      <MenuTrigger asChild>
        {inline ? (
          <button type="button" className="flex w-full items-center gap-2.5 rounded-lg p-1.5 text-left hover:bg-soft">
            <Avatar name={session.user.name} email={session.user.email} image={session.user.image} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12px] font-semibold">{session.user.name || session.user.email}</span>
              <span className="block truncate text-[10.5px] text-muted">{session.org?.name}</span>
            </span>
            <ChevronDown className="size-3.5 text-muted" />
          </button>
        ) : (
          <button type="button" aria-label="Account menu" className="rounded-full outline-offset-2 hover:outline-2 hover:outline-[#a5a4ff]">
            <Avatar name={session.user.name} email={session.user.email} image={session.user.image} />
          </button>
        )}
      </MenuTrigger>
      <MenuContent align={inline ? 'start' : 'end'} className="w-[272px]">
        <div className="flex items-center gap-2.5 px-2 py-2">
          <Avatar name={session.user.name} email={session.user.email} image={session.user.image} size={34} />
          <div className="min-w-0">
            <div className="truncate text-[12.5px] font-semibold text-ink">{session.user.name || 'Your account'}</div>
            <div className="truncate text-[11px] text-muted">{session.user.email}</div>
          </div>
        </div>
        {session.org && (
          <div className="mx-1 mb-1 rounded-lg bg-soft px-2.5 py-2">
            <div className="text-[10px] text-muted">Organisation</div>
            <div className="truncate text-[12px] font-semibold text-ink">{session.org.name}</div>
            <div className="text-[10.5px] text-muted">
              {session.role?.name} · {planLabel(session.org.plan_id)}
            </div>
          </div>
        )}
        {session.orgs.length > 1 && (
          <>
            <MenuLabel>Switch organisation</MenuLabel>
            {session.orgs.map(o => (
              <MenuItem key={o.id} onSelect={() => o.id !== session.org?.id && void switchOrg(o.id)}>
                {o.id === session.org?.id ? <Check className="!text-accent" /> : <span className="size-3.5" />}
                <span className="truncate">{o.name}</span>
                <span className="ml-auto text-[10px] text-muted">{o.role_name}</span>
              </MenuItem>
            ))}
          </>
        )}
        <MenuItem onSelect={() => void navigate({ to: '/create-org' })}>
          <Plus /> Create organisation
        </MenuItem>
        <MenuSeparator />
        <MenuItem onSelect={() => void navigate({ to: '/me/profile' })}>
          <UserRound /> Profile
        </MenuItem>
        <MenuItem onSelect={() => void navigate({ to: '/me/security' })}>
          <Shield /> Security
        </MenuItem>
        <MenuItem onSelect={() => void navigate({ to: '/me/notifications' })}>
          <Bell /> Notifications
        </MenuItem>
        <MenuItem onSelect={() => void navigate({ to: '/help' })}>
          <LifeBuoy /> Help
        </MenuItem>
        <MenuSeparator />
        <MenuItem danger onSelect={() => void signOut()}>
          <LogOut /> Sign out
        </MenuItem>
      </MenuContent>
    </Menu>
  )
}

export function planLabel(planId: string): string {
  return ({ sandbox: 'Free sandbox', team: 'Team', business: 'Business', enterprise: 'Enterprise' } as Record<string, string>)[planId] ?? planId
}
